'use client';

import { Add, Remove } from '@1debit/icons';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';
import Image from 'next/image';
import { useId, useState } from 'react';
import CTA, { isValidCta } from 'components/shared/CTA/CTA';
import { cn } from 'utils/styles';
import { TOAST_CARD_SHELL, TOAST_COLLAPSE_DURATION, TOAST_TEXT } from './consts';
import type { ToastContentProps } from './types';

/**
 * The `collapsing toast` surface: a corner card that trades the close button for
 * a collapse toggle, so the offer shrinks to its eyebrow row and stays reachable
 * instead of disappearing for the session.
 *
 * The toggle is a sibling of the panel rather than a wrapper around the card.
 * Wrapping would place the CTA inside a button, which axe reports as
 * `nested-interactive` at serious severity and fails the Playwright a11y gate.
 *
 * Opens expanded: collapsing replaces dismissal here, so starting collapsed
 * would hide the message the toast exists to deliver.
 *
 * Motion defaults its `reducedMotion` config to `never`, so the collapse needs a
 * manual gate. Without one, `prefers-reduced-motion` stops the wrapper's slide in
 * `Toast.tsx` but not this panel.
 */
const ToastCollapsibleCard = ({ category, headline, media, cta, ctaAsButton, className }: ToastContentProps) => {
  const panelId = useId();
  const [expanded, setExpanded] = useState(true);
  const prefersReducedMotion = useReducedMotion();

  return (
    <div className={cn(TOAST_CARD_SHELL, 'flex flex-col', TOAST_TEXT, className)}>
      <button
        type="button"
        onClick={() => setExpanded(current => !current)}
        aria-expanded={expanded}
        aria-controls={panelId}
        /* The category is the toggle's only text, and Contentful cannot require a
           field for one variant alone - so an author can publish a collapsible
           with none. Both icons are decorative, which would otherwise leave the
           button with no accessible name. */
        aria-label={category?.trim() ? undefined : 'Offer details'}
        className="flex w-full cursor-pointer items-center justify-between gap-4 text-left text-content-secondary">
        <span>{category}</span>
        {expanded ? <Remove aria-hidden /> : <Add aria-hidden />}
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <m.div
            id={panelId}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : TOAST_COLLAPSE_DURATION }}
            style={{ overflow: 'hidden' }}>
            {/* Spacing lives inside the animated element so the 4px gap collapses with it. */}
            <div className="mt-1 flex items-center gap-4">
              <div className="flex min-w-0 flex-1 flex-col gap-4">
                <p className="text-kale">{headline}</p>
                {isValidCta(cta) && (
                  <CTA
                    cta={cta}
                    variant={ctaAsButton ? 'solid' : 'link'}
                    className={ctaAsButton ? 'w-fit' : 'w-max text-kale'}
                    testId={ctaAsButton ? 'toastCtaButton' : undefined}
                  />
                )}
              </div>
              {media?.url && (
                <Image
                  src={media.url}
                  alt={media.description ?? ''}
                  width={72}
                  height={72}
                  className="size-[72px] shrink-0 object-contain"
                />
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ToastCollapsibleCard;
